import React from "react";
import type { SerializablePluginMetadata } from "@glide/plugins-codecs";
import { isOAuthDefinition } from "@glide/plugins-codecs";
import { trackEvent } from "@glide/common-core/dist/js/analytics";
import { performOAuthStageOne } from "@glide/common-core/dist/js/plugin-oauth-stage-one";
import { IntegrationAppClient } from "@membranehq/sdk";
import { getAppFacilities } from "@glide/common-core/dist/js/support/app-renderer";
import { getIntegrationAppToken } from "@glide/backend-api";
import { isErrorInfo } from "@glide/error-info";
import { assertNever } from "@glideapps/ts-necessities";

export async function connectPluginAuth(
    appID: string,
    ownerID: string,
    plugin: SerializablePluginMetadata,
    configIDInner: string
): Promise<void> {
    const { auth } = plugin;
    if (auth === undefined) return;
    if (isOAuthDefinition(auth)) {
        trackEvent("plugin connect started", {
            plugin_id: plugin.id,
            app_id: appID,
            auth_provider_id: auth.provider,
        });
        await performOAuthStageOne({
            authProvider: auth.provider,
            ownerID,
            appID,
            pluginID: plugin.id,
            configID: configIDInner,
            explicitScopes: auth.scopes,
        });
    } else if (auth.kind === "integration-app") {
        const eventProps = {
            plugin_id: plugin.id,
            app_id: appID,
        };
        try {
            trackEvent("plugin integration.app connect started", eventProps);
            const integrationApp = new IntegrationAppClient({
                async fetchToken() {
                    const token = await getIntegrationAppToken(appID, getAppFacilities());
                    if (isErrorInfo(token)) {
                        // FIXME: show error modal?
                        throw new Error(token.message);
                    }
                    return token;
                },
            });
            await integrationApp.integration(auth.key).openNewConnection();
            trackEvent("plugin integration.app connected", eventProps);
        } catch (e: unknown) {
            trackEvent("plugin integration.app connect failed", eventProps);
        }
    } else {
        return assertNever(auth.kind);
    }
}

export function useConnectPluginAuth(
    appID: string,
    ownerID: string,
    plugin: SerializablePluginMetadata
): (configIDInner: string) => Promise<void> {
    const connect = React.useCallback(
        async (configIDInner: string) => {
            return await connectPluginAuth(appID, ownerID, plugin, configIDInner);
        },
        [appID, ownerID, plugin]
    );
    return connect;
}
